// A branch history's track (logic/track.ts). Its main lane is the
// history's first-parent chain from its newest commit, oldest first: from
// the newest held commit, each point's first parent, while the windows
// hold it (`historyLane`). A merge's merged commits (its other parents'
// history, which GitHub lists among the branch's by date) are no points,
// so every point is a first-parent ancestor of every later one, and the
// diff between two points, the latest commit's above all (its first parent
// → it), is what the branch changed between them. The base is the first
// parent the windows do not hold (older commits exist then: `hasOlder`),
// or git's empty tree at the root commit.
//
// A history filtered to a path is GitHub's listing of the commits that
// touch it, which skips the others, so first parents cannot be followed
// through it. Decided: its lane is that listing (oldest first), and it
// breaks (`breakBefore`) before each point whose predecessor is not its
// first parent: across a break the diff between two points is still
// their trees' difference in the path, which may hold another line of the
// history's changes, and the break says so. Rejected: reading the whole
// first-parent chain to keep only its commits that touch the path (every
// commit of the branch read, to show a few).
//
// Force-pushes come from the repository's activity (state/ref/fetch.ts),
// newest first. What each replaced (its `before` commit's history the
// branch no longer has) forks off the commit both still share: hung from
// that point when the track holds it, with the main lane breaking right
// after it (where the push's new commits start); held apart (`unheld`)
// otherwise. The commit a push left the branch at says who pushed it. A
// branch's history has no reviews, so no last review.
import { EMPTY_TREE, type MainPoint, type ReplacedPoint, type Track, type UnheldRun } from './track';

/** A commit of a history, as GitHub's REST lists it (state/compare/fetch.ts `CompareCommit` is one). */
export interface HistoryCommit {
  sha: string;
  /** Its parents' SHAs, the first parent first. */
  parents: readonly string[];
  /** The whole commit message. */
  message: string;
  author: string | null;
  committed: string | null;
}

/** A force-push of a branch, as GitHub's repository activity reports it. */
export interface ForcePush {
  /** The branch's commit before the push, and after it. */
  before: string;
  after: string;
  /** Who pushed, when GitHub names them. */
  actor: string | null;
  at: string | null;
}

/** What a force-push replaced: the commits of its `before` the branch no longer has, and where they fork off it. */
export interface ReplacedRun {
  /** The newest commit both the replaced history and the branch hold; null when GitHub could not say (the commit is gone). */
  forkPoint: string | null;
  /** The newest of them, oldest first; `before` itself alone when GitHub no longer has it. */
  commits: readonly HistoryCommit[];
}

/** What a branch history's track is made from. */
export interface RefTrackInput {
  /** The commits its windows hold, newest first, as GitHub lists them. */
  commits: readonly HistoryCommit[];
  /** Older commits than those held exist. */
  hasOlder: boolean;
  /** The history is filtered to a path (GitHub lists only the commits touching it). */
  filtered: boolean;
  /** The branch's force-pushes, newest first. */
  pushes: readonly ForcePush[];
  /** What each push replaced, by its `before` commit; a push missing here is its `before` alone, held apart. */
  runs: ReadonlyMap<string, ReplacedRun>;
}

/** The first line of a commit message. */
const headline = (message: string) => message.split('\n', 1)[0];

/** The base's name: the short SHA of the commit before the window, or (the empty tree) what it is. */
export const EMPTY_TREE_NAME = 'empty tree';

/** A history's main lane, oldest first, its base, the points it breaks before, and whether it stops at a commit the windows do not hold. */
export interface HistoryLane<C extends HistoryCommit = HistoryCommit> {
  lane: C[];
  base: string;
  breaks: ReadonlySet<string>;
  cut: boolean;
}

/**
 * The main lane of `commits` (newest first, as GitHub lists them): the
 * first-parent chain from the newest, or, for a history `filtered` to a
 * path, the listing with breaks where a point's predecessor is not its
 * first parent (see the file comment). Empty for no commits.
 */
export function historyLane<C extends HistoryCommit>(commits: readonly C[], filtered: boolean): HistoryLane<C> {
  if (!commits.length) return { lane: [], base: '', breaks: new Set(), cut: false };
  if (filtered) {
    const lane = [...commits].reverse();
    const breaks = new Set(lane.filter((c, i) => i > 0 && c.parents[0] !== lane[i - 1].sha).map((c) => c.sha));
    return { lane, base: lane[0].parents[0] ?? EMPTY_TREE, breaks, cut: false };
  }
  const bySha = new Map(commits.map((c) => [c.sha, c]));
  const chain: C[] = [];
  for (let c: C | undefined = commits[0]; c && chain.length <= commits.length; c = bySha.get(c.parents[0] ?? '')) chain.push(c);
  const last = chain[chain.length - 1];
  return { lane: chain.reverse(), base: last.parents[0] ?? EMPTY_TREE, breaks: new Set(), cut: last.parents.length > 0 };
}

/** The track of a branch's history; see the file comment. A history holding no commit has an empty main lane and no base. */
export function refTrack(input: RefTrackInput): Track {
  const { lane, base: baseSha, breaks, cut } = historyLane(input.commits, input.filtered);
  if (!lane.length) return { base: { sha: '', name: 'base', replaced: [] }, main: [], head: '', unheld: [], hasOlder: false, lastPush: null, lastReview: null };
  const main: MainPoint[] = lane.map((c) => ({ sha: c.sha, short: c.sha.slice(0, 7), message: headline(c.message) || null, author: c.author, at: c.committed, breakBefore: breaks.has(c.sha), forcePushedBy: null, replaced: [] }));
  const at = new Map(main.map((p, i) => [p.sha, i]));
  const baseReplaced: ReplacedPoint[] = [];
  const unheld: UnheldRun[] = [];
  // Every commit the windows hold is the branch's, on the lane or merged into it: none was replaced.
  const seen = new Set<string>(input.commits.map((c) => c.sha));
  // Oldest push first, so each run lands in the order the pushes happened.
  for (const push of [...input.pushes].reverse()) {
    const after = at.get(push.after);
    if (after !== undefined) main[after] = { ...main[after], forcePushedBy: push.actor };
    // A push that kept its `before` on the branch (a fast-forward forced through) replaced nothing.
    if (seen.has(push.before) || push.before === baseSha) continue;
    const run = input.runs.get(push.before) ?? { forkPoint: null, commits: [{ sha: push.before, parents: [], message: '', author: null, committed: null }] };
    const replaced: ReplacedPoint[] = run.commits
      .filter((c) => !seen.has(c.sha))
      .map((c) => ({ sha: c.sha, short: c.sha.slice(0, 7), message: headline(c.message) || null, author: c.author, at: c.committed, replacedBy: push.actor }));
    if (!replaced.length) continue;
    for (const r of replaced) seen.add(r.sha);
    const fork = run.forkPoint === null ? undefined : run.forkPoint === baseSha ? -1 : at.get(run.forkPoint);
    if (fork === undefined) {
      unheld.push({ forksOff: run.forkPoint, replaced });
      continue;
    }
    if (fork < 0) baseReplaced.push(...replaced);
    else main[fork] = { ...main[fork], replaced: [...main[fork].replaced, ...replaced] };
    // The push's own commits start right after the fork point: the lane breaks there.
    if (main[fork + 1]) main[fork + 1] = { ...main[fork + 1], breakBefore: true };
  }
  const head = main[main.length - 1].sha;
  const last = input.pushes[0];
  return {
    base: { sha: baseSha, name: baseSha === EMPTY_TREE ? EMPTY_TREE_NAME : baseSha.slice(0, 7), replaced: baseReplaced },
    main,
    head,
    unheld,
    hasOlder: input.hasOlder || cut,
    // "Last push" starts at what the newest force-push replaced, when that push made the head.
    lastPush: last && last.after === head ? last.before : null,
    lastReview: null,
  };
}
